import { URLSearchParams } from 'url';
import { HostContext } from '../../embed-script/HostContext';
import i18n from '../../i18n';
import { AlertType } from '../../libs/models/AlertType';
import { store } from '../../redux/app/store';
import { addAlert, setRateLimit } from '../../redux/features/app/appSlice';
import { AuthHelper } from '../auth/AuthHelper';
import { scrubProviderHeaderValue, serializeHostContextHeader } from './hostContextHeader';

interface ServiceRequest {
    commandPath: string;
    method?: string;
    body?: unknown;
    query?: URLSearchParams;
    hostContext?: HostContext;
    // Throw a RateLimitedError on 429 (after the global warning) instead of resolving with an empty
    // payload: for callers that would otherwise read `{}` as a successful answer.
    throwOnRateLimit?: boolean;
    signal?: AbortSignal;
}

const noResponseBodyStatusCodes = [202, 204];

export const BackendServiceUrl = process.env.REACT_APP_BACKEND_URI ?? window.origin;

// Exact 401 body written by the backend's TokenDenyMiddleware for a session that back-channel
// logout has revoked. Exported because the SignalR client has to recognise the same response on
// its own (a raw WebSocket upgrade never reaches the handling below) and the two must not drift.
export const BlockedTokenResponseBody = 'This token has been blocked.';

export class RateLimitedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'RateLimitedError';
    }
}

export class NotFoundError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NotFoundError';
    }
}

export class ConflictError extends Error {
    // Parsed `reason` from the 409 body so callers match the exact code; undefined for a
    // non-JSON / reason-less body.
    constructor(
        message: string,
        public readonly reason?: string,
    ) {
        super(message);
        this.name = 'ConflictError';
    }
}

// A non-JSON 409 body is a valid "no reason" outcome, so a parse failure resolves to undefined.
// A non-string `reason` is likewise treated as absent, so callers never compare against a non-string.
const extractConflictReason = (body: string): string | undefined => {
    try {
        const reason = (JSON.parse(body) as { reason?: unknown }).reason;
        return typeof reason === 'string' ? reason : undefined;
    } catch {
        return undefined;
    }
};

export class BadRequestError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'BadRequestError';
    }
}

export class PreconditionFailedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PreconditionFailedError';
    }
}

export class ForbiddenError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ForbiddenError';
    }
}

export class BaseService {
    constructor(
        protected readonly accessToken: string,
        protected readonly serviceUrl: string = BackendServiceUrl,
        // Optional override for the backend JwtBearer scheme selector (Aia-Oidc-Provider header).
        // Only the agent/admin clients set it (to 'entra', #1994); customer calls leave it
        // undefined and the backend applies its default (vismaonline). The hostContext branch
        // below still wins when present, so the customer embed flow is unaffected.
        protected readonly oidcProvider?: string,
    ) {}

    protected readonly getResponseAsync = async <T>(request: ServiceRequest): Promise<T> => {
        const { commandPath, method, body, query, hostContext, throwOnRateLimit, signal } = request;

        const isFormData = body instanceof FormData;

        const headers = this.buildHeaders(isFormData, hostContext, commandPath);

        try {
            const requestUrl = new URL(commandPath, this.serviceUrl);
            if (query) {
                requestUrl.search = `?${query.toString()}`;
            }

            const response = await fetch(requestUrl, {
                method: method ?? 'GET',
                body: isFormData ? body : JSON.stringify(body),
                headers,
                signal,
            });

            if (!response.ok) {
                const responseText = await response.text();
                const responseDetails = responseText.split('--->');
                const errorDetails =
                    responseDetails.length > 1
                        ? `${responseDetails[0].trim()} ---> ${responseDetails[1].trim()}`
                        : responseDetails[0];
                const errorMessage = `${response.status.toString()}: ${response.statusText}${errorDetails}`;

                switch (response.status) {
                    case 504:
                        throw Object.assign(new Error('The request timed out. Please try sending your message again.'));
                    case 400:
                        throw new BadRequestError(errorMessage);
                    case 401:
                        if (responseText === BlockedTokenResponseBody) {
                            await AuthHelper.logoutForBlockedToken();
                        }
                        throw Object.assign(new Error('You are not authorized to perform this action.'));
                    case 403:
                        throw new ForbiddenError(errorMessage);

                    case 429: {
                        const retryAfter = response.headers.get('Retry-After');
                        const resetTime = retryAfter ? Date.now() + parseInt(retryAfter, 10) * 1000 : undefined;

                        store.dispatch(setRateLimit({ isLimited: true, resetTime }));
                        store.dispatch(
                            addAlert({
                                message: i18n.t('alerts.rateLimitExceeded'),
                                type: AlertType.Warning,
                            }),
                        );
                        // We've already read the body via response.text() above, so falling
                        // through to `await response.json()` below would throw a confusing
                        // "body stream already read" TypeError that surfaces as the generic
                        // "Unable to generate bot response" alert in useChat. The rate-limit
                        // warning is already dispatched; return an empty payload so the
                        // caller's askResult.variables guard short-circuits silently.
                        if (throwOnRateLimit) {
                            throw new RateLimitedError(errorMessage);
                        }
                        return {} as T;
                    }
                    case 404:
                        throw new NotFoundError(errorMessage);
                    case 409:
                        throw new ConflictError(errorMessage, extractConflictReason(responseText));
                    case 412:
                        throw new PreconditionFailedError(errorMessage);
                    default:
                        throw new Error(errorMessage);
                }
            }

            // A successful response can legitimately carry no body — e.g. the feedback endpoint
            // returns 200 with Content-Length 0. Parsing an empty body with response.json() throws
            // a SyntaxError that surfaces to callers as a spurious failure (the inline-header
            // feedback path alerts on any error, so an empty 200 looked like a failed save even
            // though the write succeeded). The 202/204 allowlist only covered the codes that are
            // *defined* to have no body; guard on the actual body instead so any empty-body success
            // is handled.
            if (noResponseBodyStatusCodes.includes(response.status)) {
                return {} as T;
            }
            const responseText = await response.text();
            return (responseText.length ? JSON.parse(responseText) : {}) as T;
        } catch (e: any) {
            // If it's already a typed error (ConflictError, NotFoundError, BadRequestError,
            // PreconditionFailedError, ForbiddenError), preserve it so callers can branch on instance type.
            if (
                e instanceof ConflictError ||
                e instanceof RateLimitedError ||
                e instanceof NotFoundError ||
                e instanceof BadRequestError ||
                e instanceof PreconditionFailedError ||
                e instanceof ForbiddenError
            ) {
                throw e; // Re-throw the original typed error
            }

            let additionalErrorMsg = '';
            if (e instanceof TypeError) {
                // fetch() will reject with a TypeError when a network error is encountered.
                additionalErrorMsg =
                    '\n\nPlease check that your backend is running and that it is accessible by the app';
            }
            throw Object.assign(new Error(`${e as string} ${additionalErrorMsg}`));
        }
    };

    // Like getResponseAsync but returns the raw status/headers/body instead of throwing on a non-2xx
    // or dispatching a global alert. Needed when the caller must inspect a 4xx itself: the export
    // endpoint returns 409 with a next-eligible time, and a 429 here must NOT fire the global
    // rate-limit alert getResponseAsync raises. A network failure still rejects (fetch throws).
    // The 401 "This token has been blocked." → logout that getResponseAsync performs is deliberately
    // omitted here: this helper is only used by the export path, and a blocked token there just yields
    // a generic 'error' result — the next ordinary getResponseAsync call logs the user out anyway.
    protected readonly getResponseWithHeadersAsync = async (
        request: ServiceRequest,
    ): Promise<{ status: number; headers: Headers; data: unknown }> => {
        const { commandPath, method, body, query, hostContext } = request;

        const isFormData = body instanceof FormData;
        const headers = this.buildHeaders(isFormData, hostContext, commandPath);

        const requestUrl = new URL(commandPath, this.serviceUrl);
        if (query) {
            requestUrl.search = `?${query.toString()}`;
        }

        const response = await fetch(requestUrl, {
            method: method ?? 'GET',
            body: isFormData ? body : JSON.stringify(body),
            headers,
        });

        const responseText = await response.text();
        let data: unknown;
        if (responseText.length) {
            try {
                data = JSON.parse(responseText) as unknown;
            } catch {
                // Non-JSON body (e.g. a plain-text error). Leave data undefined; the caller branches
                // on status, not body.
                data = undefined;
            }
        }

        return { status: response.status, headers: response.headers, data };
    };

    // Shared request-header construction for both response helpers: bearer auth, JSON content-type,
    // and the hostContext / Aia-Oidc-Provider scheme selection.
    private buildHeaders(isFormData: boolean, hostContext?: HostContext, commandPath?: string): Headers {
        const headers = new Headers(
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            this.accessToken
                ? {
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      Authorization: `Bearer ${this.accessToken}`,
                  }
                : undefined,
        );

        if (!isFormData) {
            headers.append('Content-Type', 'application/json');
        }

        if (hostContext) {
            // Log warning if hostContext.id is missing - this shouldn't happen
            if (!hostContext.id) {
                console.warn('BaseService: hostContext.id was null/undefined. This may indicate a storage issue.', {
                    originalContext: hostContext,
                    url: commandPath,
                    hasHostname: !!hostContext.hostname,
                    hasProducts: !!hostContext.products?.length,
                });
            }

            // Scrub code points that would truncate or reject the header value (#2383); the host's
            // <h1> title is arbitrary content and JSON.stringify leaves C1 controls / line
            // separators raw.
            headers.append('AiAHostContext', serializeHostContextHeader(hostContext));
            // hostContext.id is host-supplied and unbounded too, so it passes through a scrub-and-cap:
            // a code point > U+00FF would otherwise make Headers.append throw and sink the whole
            // request, and an unbounded id could emit a multi-KB provider header a server rejects (#2383).
            headers.append('Aia-Oidc-Provider', scrubProviderHeaderValue(hostContext.id));
        }

        // Agent/admin clients (no hostContext) select their scheme explicitly. Don't overwrite a
        // provider already set from hostContext above.
        if (this.oidcProvider && !headers.has('Aia-Oidc-Provider')) {
            headers.append('Aia-Oidc-Provider', this.oidcProvider);
        }

        return headers;
    }
}
